import React from 'react';
import { Download, TrendingUp, Globe, Users, DollarSign, Target, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { mockData } from '../mock';

const Investor = ({ currentLang }) => {
  const t = mockData.translations[currentLang];

  const handleDownload = (type) => {
    // Mock download functionality
    alert(`Downloading ${type}... (Mock functionality)`);
  };

  return (
    <div className="bg-black min-h-screen pt-24">
      {/* Hero Section */}
      <section className="py-20">
        <div className="px-[7.6923%]">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-6xl font-bold text-white mb-6">
              Investor <span className="text-[#00FFD1]">Relations</span>
            </h1>
            <p className="text-xl text-white/85 leading-relaxed mb-8">
              Partner with us in shaping the future of Southeast Asia's technology landscape
            </p>
            <div className="bg-white/5 border border-white/20 p-6 inline-block">
              <p className="text-2xl font-bold text-[#00FFD1] mb-2">Current Fundraising</p>
              <p className="text-4xl font-bold text-white">{mockData.company.equityRange}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Investment Thesis */}
      <section className="py-20 border-t border-white/25">
        <div className="px-[7.6923%]">
          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <h2 className="text-4xl font-bold text-white mb-6">Investment Thesis</h2>
              <p className="text-lg text-white/85 leading-relaxed mb-6">
                Southeast Asia represents the world's fastest-growing digital economy, with Indonesia at its center. The convergence of AI, blockchain, and fintech creates unprecedented opportunities for value creation.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-[#00FFD1] rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-white/70">Market size projected to reach $1 trillion by 2030</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-[#00FFD1] rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-white/70">280+ million underbanked population in Indonesia alone</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-[#00FFD1] rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-white/70">Government support for digital transformation initiatives</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-[#00FFD1] rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-white/70">Early-stage AI and blockchain adoption across industries</p>
                </div>
              </div>
            </div>
            
            <div>
              <img 
                src={mockData.images.jakartaNight}
                alt="Jakarta Night Skyline"
                className="w-full h-80 object-cover border border-white/20"
              />
              <p className="text-white/60 text-sm mt-4 text-center">
                Jakarta - Southeast Asia's Financial Hub
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Key Metrics */}
      <section className="py-20 border-t border-white/25">
        <div className="px-[7.6923%]">
          <h2 className="text-4xl font-bold text-white text-center mb-16">Fund Overview</h2>
          
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-[#00FFD1]/20 rounded-lg flex items-center justify-center">
                <DollarSign className="w-8 h-8 text-[#00FFD1]" />
              </div>
              <h3 className="text-3xl font-bold text-white mb-2">$85-105M</h3>
              <p className="text-white/70">Fund Size</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-[#00FFD1]/20 rounded-lg flex items-center justify-center">
                <Target className="w-8 h-8 text-[#00FFD1]" />
              </div>
              <h3 className="text-3xl font-bold text-white mb-2">15-20</h3>
              <p className="text-white/70">Portfolio Companies</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-[#00FFD1]/20 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-8 h-8 text-[#00FFD1]" />
              </div>
              <h3 className="text-3xl font-bold text-white mb-2">3-7x</h3>
              <p className="text-white/70">Target Returns</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-[#00FFD1]/20 rounded-lg flex items-center justify-center">
                <Globe className="w-8 h-8 text-[#00FFD1]" />
              </div>
              <h3 className="text-3xl font-bold text-white mb-2">5-7 Years</h3>
              <p className="text-white/70">Investment Period</p>
            </div>
          </div>
        </div>
      </section>

      {/* Investment Focus */}
      <section className="py-20 border-t border-white/25">
        <div className="px-[7.6923%]">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-white text-center mb-16">Sector Allocation</h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              {[
                { sector: "AI & Machine Learning", percentage: "35%", description: "Predictive analytics, automation platforms, computer vision" },
                { sector: "Blockchain & Crypto", percentage: "30%", description: "DeFi protocols, tokenization platforms, cryptocurrency infrastructure" },
                { sector: "Fintech Solutions", percentage: "25%", description: "Digital payments, mobile banking, microfinance platforms" },
                { sector: "Smart Infrastructure", percentage: "10%", description: "IoT platforms, smart city solutions, industrial automation" }
              ].map((item, index) => (
                <div key={index} className="bg-white/5 border border-white/20 p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold text-white">{item.sector}</h3>
                    <span className="text-2xl font-bold text-[#00FFD1]">{item.percentage}</span>
                  </div>
                  <p className="text-white/70">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Documents Download */}
      <section className="py-20 border-t border-white/25">
        <div className="px-[7.6923%]">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-white mb-6">Investment Materials</h2>
            <p className="text-xl text-white/70 mb-12">
              Access detailed information about our investment strategy and fund performance
            </p>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white/5 border border-white/20 p-6 text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-[#00FFD1]/20 rounded-lg flex items-center justify-center">
                  <FileText className="w-8 h-8 text-[#00FFD1]" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">Pitch Deck</h3>
                <p className="text-white/70 mb-6">
                  Comprehensive overview of our investment thesis and strategy
                </p>
                <Button 
                  onClick={() => handleDownload('Pitch Deck')}
                  className="bg-[#00FFD1] text-black hover:bg-[#00FFD1]/90 border-none rounded-none w-full"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </Button>
              </div>
              
              <div className="bg-white/5 border border-white/20 p-6 text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-[#00FFD1]/20 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-8 h-8 text-[#00FFD1]" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">Financial Highlights</h3>
                <p className="text-white/70 mb-6">
                  Key financial metrics and performance indicators
                </p>
                <Button 
                  onClick={() => handleDownload('Financial Highlights')}
                  className="bg-[#00FFD1] text-black hover:bg-[#00FFD1]/90 border-none rounded-none w-full"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </Button>
              </div>
              
              <div className="bg-white/5 border border-white/20 p-6 text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-[#00FFD1]/20 rounded-lg flex items-center justify-center">
                  <Users className="w-8 h-8 text-[#00FFD1]" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">Team Profiles</h3>
                <p className="text-white/70 mb-6">
                  Detailed backgrounds of our investment team
                </p>
                <Button 
                  onClick={() => handleDownload('Team Profiles')}
                  className="bg-[#00FFD1] text-black hover:bg-[#00FFD1]/90 border-none rounded-none w-full"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 border-t border-white/25">
        <div className="px-[7.6923%]">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-white mb-6">Ready to Invest?</h2>
            <p className="text-xl text-white/70 mb-8">
              Connect with our investor relations team to learn more about partnership opportunities
            </p>
            
            <Button 
              asChild
              className="bg-[#00FFD1] text-black hover:bg-[#00FFD1]/90 border-none rounded-none py-4 px-8 text-lg font-medium min-h-[56px]"
            >
              <Link to="/contact">
                Contact Investor Relations
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Investor;